import { useEffect, useState } from 'react';
import {
  LEGACY_DOCUMENT_LABELS,
  legacyDocumentType,
  type LegacyDocumentDetail,
  type LegacyDocumentsResponse,
  type LegacyDocumentType,
} from '@hesabat/contracts';
import { legacyApi, ProblemError, UnauthorizedError } from './api';
import Hint from './Hint';
import { formatMoney, isZero } from './money';
import { legacyDocument, PrintPreview } from './PrintDocument';

interface Props {
  companyId: string;
  currency: string;
  /** داخل كشف طرف: مستنداته وحده، بلا عنوان الشاشة، وتختفى لو مفيش. */
  party?: { type: 'customer' | 'supplier'; id: string };
  onSessionLost: () => void;
}

const PAGE = 100;

/**
 * أرشيف البرنامج السابق: كل فاتورة وسند اترحّل بتاريخه وأصنافه، للبحث والعرض والطباعة.
 * القراءة فقط — الأصل فى البرنامج القديم، وما قبل تاريخ الترحيل محمول فى الرصيد الافتتاحى.
 */
export default function LegacyArchive({ companyId, currency, party, onSessionLost }: Props) {
  const [filters, setFilters] = useState({ type: '', from: '', to: '', q: '' });
  const [query, setQuery] = useState(filters);
  const [page, setPage] = useState(0);
  const [result, setResult] = useState<LegacyDocumentsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState<LegacyDocumentDetail | null>(null);

  // البحث بعد توقف الكتابة — لا طلب مع كل حرف
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(filters);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    let alive = true;
    legacyApi
      .list(companyId, {
        partyType: party?.type,
        partyId: party?.id,
        type: query.type || undefined,
        from: query.from || undefined,
        to: query.to || undefined,
        q: query.q.trim() || undefined,
        limit: String(PAGE),
        offset: String(page * PAGE),
      })
      .then((value) => {
        if (alive) setResult(value);
      })
      .catch((caught: unknown) => {
        if (!alive) return;
        if (caught instanceof UnauthorizedError) onSessionLost();
        else if (caught instanceof ProblemError)
          setError(caught.problem.detail ?? caught.problem.title);
        else setError(caught instanceof Error ? caught.message : String(caught));
      });
    return () => {
      alive = false;
    };
  }, [companyId, party?.type, party?.id, query, page, onSessionLost]);

  async function open(documentId: string): Promise<void> {
    try {
      setPrinting(await legacyApi.get(companyId, documentId));
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setError(caught instanceof Error ? caught.message : String(caught));
    }
  }

  const filtered = Object.values(query).some(Boolean);
  // داخل الكشف: لو الطرف مالوش مستندات قديمة أصلاً، مفيش قسم
  if (party && result && result.total === 0 && !filtered) return null;
  const pages = result ? Math.ceil(result.total / PAGE) : 0;

  return (
    <div className="panel">
      <div className="panel-head">
        {party ? <h3>مستندات البرنامج السابق</h3> : <h2>أرشيف البرنامج القديم</h2>}
      </div>
      <Hint block>
        الفواتير والسندات اللى اترحّلت من البرنامج القديم بتواريخها وأصنافها. اضغط «عرض» تشوف
        المستند وتطبعه. اللى قبل تاريخ الترحيل أرشيف للعرض وقيمته داخلة فى الرصيد الافتتاحى، واللى
        بعده متسجل له قيد.
      </Hint>
      <div className="form-row">
        <label className="grow">
          بحث برقم المستند أو الاسم أو البيان
          <input
            value={filters.q}
            onChange={(event) => setFilters({ ...filters, q: event.target.value })}
          />
        </label>
        <label>
          النوع
          <select
            value={filters.type}
            onChange={(event) => setFilters({ ...filters, type: event.target.value })}
          >
            <option value="">الكل</option>
            {legacyDocumentType.options.map((type) => (
              <option key={type} value={type}>
                {LEGACY_DOCUMENT_LABELS[type]}
              </option>
            ))}
          </select>
        </label>
        <label>
          من
          <input
            type="date"
            value={filters.from}
            onChange={(event) => setFilters({ ...filters, from: event.target.value })}
          />
        </label>
        <label>
          إلى
          <input
            type="date"
            value={filters.to}
            onChange={(event) => setFilters({ ...filters, to: event.target.value })}
          />
        </label>
      </div>
      {error && <p className="error">{error}</p>}
      {result && (
        <>
          <p className="sub">
            {result.total} مستند — إجمالى المبالغ {formatMoney(result.totalAmount)} {currency}
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>النوع</th>
                  <th>رقم المستند</th>
                  {!party && <th>الطرف</th>}
                  <th>المبلغ</th>
                  <th>المدفوع</th>
                  <th>البيان</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {result.documents.map((doc) => (
                  <tr key={doc.id}>
                    <td>{doc.documentDate}</td>
                    <td>{LEGACY_DOCUMENT_LABELS[doc.type as LegacyDocumentType]}</td>
                    <td className="mono">{doc.reference ?? ''}</td>
                    {!party && <td>{doc.partyName ?? ''}</td>}
                    <td className="num-cell">{formatMoney(doc.amount)}</td>
                    <td className="num-cell">{isZero(doc.paid) ? '' : formatMoney(doc.paid)}</td>
                    <td>{doc.memo ?? ''}</td>
                    <td>
                      {doc.journalId ? (
                        <span className="pill">مُقيَّد</span>
                      ) : (
                        <span className="pill muted">أرشيف</span>
                      )}
                    </td>
                    <td>
                      <button type="button" className="ghost" onClick={() => void open(doc.id)}>
                        عرض{doc.lineCount > 0 ? ' (' + doc.lineCount + ' صنف)' : ''}
                      </button>
                    </td>
                  </tr>
                ))}
                {result.documents.length === 0 && (
                  <tr>
                    <td colSpan={party ? 8 : 9} className="sub">
                      {filtered
                        ? 'مفيش مستندات بالفلتر ده.'
                        : 'مفيش مستندات من البرنامج القديم — ارفع نسخة البرنامج القديم من «ترحيل البيانات».'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="form-actions">
              <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>
                السابق
              </button>
              <span className="sub">
                صفحة {page + 1} من {pages}
              </span>
              <button type="button" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
                التالى
              </button>
            </div>
          )}
        </>
      )}
      {printing && (
        <PrintPreview
          companyId={companyId}
          doc={legacyDocument(printing, currency)}
          onClose={() => setPrinting(null)}
          onSessionLost={onSessionLost}
        />
      )}
    </div>
  );
}
